import {
  faArrowLeft,
  faSave,
  faUser,
  faStethoscope,
  faEye,
  faPrint,
  faFileText
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import AutoComplete from '../components/AutoComplete';
import NotificationModal from '../components/NotificationModal';
import PageHeader from '../components/PageHeader';
import useConsultas from '../hooks/useConsultas';
import usePacientes from '../hooks/usePacientes';
import { calculateAge } from '../shared/age';
import Masks from '../shared/masks';
import { Consulta, TinyPacient } from '../types';
import Button from '../components/Button';
import Input from '../components/Input';
import FormInput from '../components/FormInput';
import FormTextarea from '../components/FormTextarea';
import FormCheckbox from '../components/FormCheckbox';
import FormRadio from '../components/FormRadio';
import Alert from '../components/Alert';
import { consultaSchema, ConsultaFormData } from '../schemas';

interface AutoCompleteItem {
  key: string | number;
  display: string;
}

interface NotificationState {
  type: 'success' | 'error';
  title: string;
  message: string;
}

const emptyConsulta: Consulta = {
  id: '',
  paciente_id: '',
  queixa: '',
  cirurgia_oftalmologica: false,
  dm: false,
  has: false,
  glaucoma_familia: false,
  refracao: undefined,
  od_esferico: null,
  od_cilindrico: null,
  od_eixo: null,
  od_av: '',
  oe_esferico: null,
  oe_cilindrico: null,
  oe_eixo: null,
  oe_av: '',
  adicao: null,
  biomicroscopia: '',
  fundo_de_olho: '',
  pio: '',
  conduta: ''
};

const emptyForm: ConsultaFormData = {
  paciente_id: '',
  queixa: '',
  cirurgia_oftalmologica: false,
  dm: false,
  has: false,
  glaucoma_familia: false,
  refracao: undefined,
  od_esferico: '',
  od_cilindrico: '',
  od_eixo: '',
  od_av: '',
  oe_esferico: '',
  oe_cilindrico: '',
  oe_eixo: '',
  oe_av: '',
  adicao: '',
  biomicroscopia: '',
  fundo_de_olho: '',
  pio: '',
  conduta: ''
};

export default function ConsultasById() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { consulta = emptyConsulta, error, getById, upsert, loading } = useConsultas();
  const { pacientes, get, clear } = usePacientes();
  const [pacienteBusca, setPacienteBusca] = useState<string>('');
  const [pacienteSelected, setPacienteSelected] = useState<TinyPacient>({
    id: '',
    nome: '',
    data_nasc: ''
  });
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { DecimalMask, CilindricoMask, NumberToDecimalMask, DecimalToNumber } = useMemo(
    () => Masks(),
    []
  );
  const isEditing = id !== 'create';

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid }
  } = useForm<ConsultaFormData>({
    resolver: zodResolver(consultaSchema),
    mode: 'onChange',
    defaultValues: emptyForm
  });

  useEffect(() => {
    if (id && id !== 'create') getById(id);
  }, [id, getById]);

  // Reset form when consulta data is loaded
  useEffect(() => {
    if (consulta && Object.keys(consulta).length > 0) {
      // Transform consulta data to match form schema
      const formData: ConsultaFormData = {
        paciente_id: consulta.paciente_id?.toString() || '',
        queixa: consulta.queixa || '',
        cirurgia_oftalmologica: consulta.cirurgia_oftalmologica || false,
        dm: consulta.dm || false,
        has: consulta.has || false,
        glaucoma_familia: consulta.glaucoma_familia || false,
        refracao:
          consulta.refracao === 'dinamica' || consulta.refracao === 'estatica'
            ? consulta.refracao
            : undefined,
        od_esferico: NumberToDecimalMask(consulta.od_esferico),
        od_cilindrico: NumberToDecimalMask(consulta.od_cilindrico),
        od_eixo: consulta.od_eixo?.toString() || '',
        od_av: consulta.od_av || '',
        oe_esferico: NumberToDecimalMask(consulta.oe_esferico),
        oe_cilindrico: NumberToDecimalMask(consulta.oe_cilindrico),
        oe_eixo: consulta.oe_eixo?.toString() || '',
        oe_av: consulta.oe_av || '',
        adicao: NumberToDecimalMask(consulta.adicao),
        biomicroscopia: consulta.biomicroscopia || '',
        fundo_de_olho: consulta.fundo_de_olho || '',
        pio: consulta.pio || '',
        conduta: consulta.conduta || ''
      };
      reset(formData);
    }
  }, [NumberToDecimalMask, consulta, reset]);

  useEffect(() => {
    if (!pacienteBusca) return;
    get(0, 5, pacienteBusca);
  }, [pacienteBusca, get]);

  useEffect(() => {
    if (!consulta?.id) return;
    if (consulta?.paciente) {
      setPacienteSelected(consulta.paciente);
    }
  }, [consulta]);

  const handlePacienteBusca = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPacienteSelected({ id: '', nome: '' });
    setPacienteBusca(e.target.value);
  };

  const handleAutoCompleteSelect = (selected: AutoCompleteItem): void => {
    const paciente = pacientes.find(paciente => String(paciente.id) === String(selected.key));
    if (paciente) {
      setValue('paciente_id', String(selected.key));
      setPacienteBusca('');
      setPacienteSelected(paciente);
      clear();
    }
  };

  const handlePrint = (): void => {
    if (id) {
      navigate(`/consultas/print/${id}`);
    }
  };

  const onSubmit = async (
    data: ConsultaFormData,
    shouldNavigateBack: boolean = false
  ): Promise<void> => {
    setIsSubmitting(true);

    try {
      const consultaData: Consulta = {
        ...data,
        id: isEditing ? consulta.id : '',
        // Convert values back to numbers for database
        od_esferico: data.od_esferico ? DecimalToNumber(data.od_esferico) : null,
        od_cilindrico: data.od_cilindrico ? DecimalToNumber(data.od_cilindrico) : null,
        od_eixo: data.od_eixo ? parseFloat(data.od_eixo) : null,
        oe_esferico: data.oe_esferico ? DecimalToNumber(data.oe_esferico) : null,
        oe_cilindrico: data.oe_cilindrico ? DecimalToNumber(data.oe_cilindrico) : null,
        oe_eixo: data.oe_eixo ? parseFloat(data.oe_eixo) : null,
        adicao: data.adicao ? DecimalToNumber(data.adicao) : null
      };

      const result: Consulta | undefined = await upsert(consultaData);
      console.log({ result });
      if (result) {
        setNotification({
          type: 'success',
          title: 'Sucesso!',
          message: `Consulta ${!isEditing ? 'cadastrada' : 'atualizada'} com sucesso.`
        });

        if (shouldNavigateBack) {
          setTimeout(() => {
            navigate('/consultas');
          }, 1500);
        } else if (!isEditing && result.id) {
          setTimeout(() => {
            navigate(`/consultas/${result.id}`);
          }, 1500);
        }
      }
    } catch (error) {
      setNotification({
        type: 'error',
        title: 'Erro ao Salvar',
        message: (error as Error).message || 'Ocorreu um erro inesperado. Tente novamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = handleSubmit(data => onSubmit(data, false));
  const handleSaveAndBack = handleSubmit(data => onSubmit(data, true));
  const handleBack = () => navigate('/consultas');

  if (loading && isEditing) {
    return (
      <main className="w-full flex-grow p-6">
        <div className="text-center py-12">
          <img className="w-12 h-12 mx-auto mb-4" src="/loading.svg" alt="loading" />
          <p className="text-gray-600">Carregando dados da consulta...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full flex-grow p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <PageHeader
          title={isEditing ? 'Editar Consulta' : 'Nova Consulta'}
          subtitle={
            isEditing
              ? 'Atualize as informações da consulta'
              : 'Cadastre uma nova consulta no sistema'
          }
          backLink="/consultas"
        />

        <form onSubmit={handleSubmit(data => onSubmit(data, false))} className="space-y-8">
          {/* Paciente Information Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faUser} className="text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Dados Básicos</h2>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Input
                    id="name"
                    label="Paciente"
                    required
                    value={pacienteBusca || pacienteSelected.nome || ''}
                    onChange={handlePacienteBusca}
                    placeholder="Digite o nome do paciente para buscar..."
                    autoComplete="off"
                  />
                  {/* Hidden form field for paciente_id validation */}
                  <FormInput name="paciente_id" control={control} type="hidden" />
                  <AutoComplete
                    items={pacientes
                      .filter(paciente => paciente.id && paciente.nome)
                      .map(paciente => ({ key: paciente.id!, display: paciente.nome! }))}
                    onClose={() => clear()}
                    onSelect={handleAutoCompleteSelect}
                  />
                </div>
                <div>
                  <Input
                    id="paciente"
                    label="Idade"
                    value={
                      pacienteSelected.data_nasc && calculateAge(pacienteSelected.data_nasc)
                        ? `${calculateAge(pacienteSelected.data_nasc)} anos`
                        : 'Data não informada'
                    }
                    disabled
                  />
                </div>
              </div>

              <FormTextarea
                name="queixa"
                control={control}
                id="queixa"
                label="Queixa Principal"
                required
                rows={3}
                placeholder="Descreva a queixa principal do paciente..."
              />
            </div>
          </div>

          {/* Medical History Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faStethoscope} className="text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Histórico Médico</h2>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4">Condições Médicas</h3>
                  <div className="space-y-4">
                    <FormCheckbox
                      name="cirurgia_oftalmologica"
                      control={control}
                      id="cirurgia_oftalmologica"
                      label="Cirurgia Oftalmológica"
                    />
                    <FormCheckbox
                      name="dm"
                      control={control}
                      id="dm"
                      label="Diabetes Mellitus (DM)"
                    />
                    <FormCheckbox
                      name="has"
                      control={control}
                      id="has"
                      label="Hipertensão Arterial Sistêmica (HAS)"
                    />
                    <FormCheckbox
                      name="glaucoma_familia"
                      control={control}
                      id="glaucoma_familia"
                      label="Glaucoma na Família"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4">Tipo de Refração</h3>
                  <div className="space-y-4">
                    <FormRadio
                      name="refracao"
                      control={control}
                      id="refracao_dinamica"
                      value="dinamica"
                      label="Refração Dinâmica"
                    />
                    <FormRadio
                      name="refracao"
                      control={control}
                      id="refracao_estatica"
                      value="estatica"
                      label="Refração Estática"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Vision Measurements Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faEye} className="text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Medições de Refração</h2>
              </div>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-2">
                        <span className="text-sm font-medium text-gray-900">OD</span>
                      </td>
                      <td className="py-3 px-2">
                        <FormInput
                          name="od_esferico"
                          control={control}
                          id="od_esferico"
                          label="Esférico OD"
                          type="text"
                          placeholder="0,00"
                          maskFn={DecimalMask}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <FormInput
                          name="od_cilindrico"
                          control={control}
                          id="od_cilindrico"
                          label="Cilíndrico OD"
                          type="text"
                          placeholder="-0,00"
                          maskFn={CilindricoMask}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <FormInput
                          name="od_eixo"
                          control={control}
                          id="od_eixo"
                          label="Eixo OD"
                          type="number"
                          min="0"
                          max="180"
                          placeholder="90°"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <FormInput
                          name="od_av"
                          control={control}
                          id="od_av"
                          label="AV OD"
                          labelBadge="Melhorado"
                          labelTooltip="Agora aceita formatos como 20/20, 20/40, CD 2m, etc."
                          placeholder="20/20, 20/40, CD 2m, etc."
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-2">
                        <span className="text-sm font-medium text-gray-900">OE</span>
                      </td>
                      <td className="py-3 px-2">
                        <FormInput
                          name="oe_esferico"
                          control={control}
                          id="oe_esferico"
                          label="Esférico OE"
                          type="text"
                          placeholder="0,00"
                          maskFn={DecimalMask}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <FormInput
                          name="oe_cilindrico"
                          control={control}
                          id="oe_cilindrico"
                          label="Cilíndrico OE"
                          type="text"
                          placeholder="-0,00"
                          maskFn={CilindricoMask}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <FormInput
                          name="oe_eixo"
                          control={control}
                          id="oe_eixo"
                          label="Eixo OE"
                          type="number"
                          min="0"
                          max="180"
                          placeholder="90°"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <FormInput
                          name="oe_av"
                          control={control}
                          id="oe_av"
                          label="AV OE"
                          labelBadge="Melhorado"
                          labelTooltip="Agora aceita formatos como 20/20, 20/40, CD 2m, etc."
                          placeholder="20/20, 20/40, CD 2m, etc."
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-4">
                  <FormInput
                    name="adicao"
                    control={control}
                    id="adicao"
                    label="Adição"
                    type="text"
                    placeholder="0,00"
                    maskFn={DecimalMask}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Examination Findings Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faStethoscope} className="text-orange-600" />
                <h2 className="text-lg font-semibold text-gray-900">Achados do Exame</h2>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <FormTextarea
                name="biomicroscopia"
                control={control}
                id="biomicroscopia"
                label="Biomicroscopia"
                rows={4}
                placeholder="Descreva os achados da biomicroscopia..."
              />

              <FormTextarea
                name="fundo_de_olho"
                control={control}
                id="fundo_de_olho"
                label="Fundo de Olho"
                rows={4}
                placeholder="Descreva os achados da fundoscopia..."
              />

              <FormTextarea
                name="pio"
                control={control}
                id="pio"
                label="PIO (Pressão Intraocular)"
                rows={3}
                placeholder="Registre os valores da pressão intraocular..."
              />
            </div>
          </div>

          {/* Treatment Plan Section */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faFileText} className="text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">Conduta e Tratamento</h2>
              </div>
            </div>

            <div className="p-6">
              <FormTextarea
                name="conduta"
                control={control}
                id="conduta"
                label="Conduta Médica"
                rows={5}
                placeholder="Descreva a conduta médica, prescrições e orientações ao paciente..."
              />
            </div>
          </div>

          {/* Error Display */}
          {error && <Alert variant="error" title="Erro ao salvar consulta" message={error} />}

          {/* Action Buttons */}
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting || !isValid}
                variant="primary"
                leftIcon={!isSubmitting ? faSave : undefined}
                loading={isSubmitting}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>

              <Button
                type="button"
                onClick={handleSaveAndBack}
                disabled={isSubmitting || !isValid}
                variant="success"
                leftIcon={!isSubmitting ? faSave : undefined}
                loading={isSubmitting}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar e Voltar'}
              </Button>

              {id !== 'create' && (
                <Button
                  type="button"
                  onClick={handlePrint}
                  className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-500"
                  leftIcon={faPrint}
                >
                  Imprimir
                </Button>
              )}

              <Button type="button" onClick={handleBack} variant="secondary" leftIcon={faArrowLeft}>
                Voltar sem Salvar
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Notification Modal */}
      {notification && (
        <NotificationModal
          title={notification.title}
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </main>
  );
}
